import dayjs from "dayjs";
import { makeAutoObservable, runInAction } from "mobx";
import { REPORT_GROUP_BY_LABELS } from "@/types/company/report.common";
import { excelSheet, exportExcel } from "@/utils/excel";
import { onB2BError } from "@/utils/b2bErrors";
const vehicleExcelLabel = (vehicle) => {
    if (!vehicle)
        return "—";
    const model = [vehicle.brand, vehicle.model].filter(Boolean).join(" ");
    if (!model)
        return vehicle.stateNumber ?? "—";
    return vehicle.stateNumber ? `${model}, ${vehicle.stateNumber}` : model;
};
const defaultParams = () => ({
    dateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
    dateTo: dayjs().endOf("month").format("YYYY-MM-DD"),
    groupBy: "total",
});
/**
 * Отчёт по заказам компании (UC1124, WP-DSP-2 P-8). Доступен обеим ролям персонала (DISPATCHER,
 * ADMINISTRATOR — BR-RPT-03, D11) и корп-админу по своей компании (K-4): стор зависит только от
 * `ICompanyReportService`, права решает вызывающая сторона.
 */
export class CompanyReportStore {
    constructor(reportService) {
        this.reportService = reportService;
        this.companyId = null;
        this.params = defaultParams();
        this.report = null;
        this.orders = [];
        this.isLoading = false;
        this.isExporting = false;
        this.generation = 0;
        makeAutoObservable(this, {
            reportService: false,
            generation: false,
        });
    }
    async init(companyId) {
        this.dispose();
        this.companyId = companyId;
        await this.load();
    }
    setParams(params) {
        this.params = { ...this.params, ...params };
        void this.load();
    }
    async load() {
        const companyId = this.companyId;
        if (!companyId)
            return;
        const generation = this.generation;
        this.isLoading = true;
        try {
            const [report, orders] = await Promise.all([
                this.reportService.getReport(companyId, this.params),
                this.reportService.listOrders(companyId, this.params.dateFrom, this.params.dateTo),
            ]);
            runInAction(() => {
                if (generation !== this.generation)
                    return;
                this.report = report;
                this.orders = orders;
            });
        }
        catch (error) {
            onB2BError(error, "Не удалось загрузить отчёт по заказам компании");
        }
        finally {
            runInAction(() => {
                if (generation === this.generation)
                    this.isLoading = false;
            });
        }
    }
    /** `corp-report-export` (§10.2) — сводка (шапка + разрез + итого) и реестр заказов в `.xlsx`. */
    async exportExcel(companyName) {
        this.isExporting = true;
        try {
            const report = this.report;
            const totals = report?.totals ?? { orders: 0, kwh: 0, amount: 0 };
            const headerRows = [
                { label: `Компания: ${companyName}`, orders: null, kwh: null, amount: null },
                { label: `Период с: ${this.params.dateFrom}`, orders: null, kwh: null, amount: null },
                { label: `Период по: ${this.params.dateTo}`, orders: null, kwh: null, amount: null },
                { label: `Разрез: ${REPORT_GROUP_BY_LABELS[this.params.groupBy]}`, orders: null, kwh: null, amount: null },
            ];
            const breakdownRows = (report?.rows ?? []).map((row) => ({
                label: row.label,
                orders: row.orders,
                kwh: row.kwh,
                amount: row.amount,
            }));
            const totalRow = { label: "Итого", orders: totals.orders, kwh: totals.kwh, amount: totals.amount };
            const summaryColumns = [
                { header: "Строка", value: (row) => row.label },
                { header: "Заказов", value: (row) => row.orders, type: "number" },
                { header: "кВт⋅ч", value: (row) => row.kwh, type: "number" },
                { header: "Сумма", value: (row) => row.amount, type: "number" },
            ];
            const registryColumns = [
                {
                    header: "Завершён",
                    value: (order) => (order.chargedAt ? new Date(order.chargedAt) : null),
                    type: "date",
                    format: "dd.mm.yyyy hh:mm",
                },
                { header: "Номер", value: (order) => order.orderNumber },
                { header: "Сотрудник", value: (order) => order.employeeLabel ?? order.clientName },
                { header: "Адрес", value: (order) => order.address },
                { header: "Автомобиль", value: (order) => vehicleExcelLabel(order.vehicle) },
                { header: "кВт⋅ч", value: (order) => order.actualTotalKwh, type: "number" },
                { header: "Цена", value: (order) => order.pricePerKwhSnapshot, type: "number" },
                { header: "Сбор", value: (order) => order.serviceFeeSnapshot, type: "number" },
                { header: "Сумма", value: (order) => order.actualCost, type: "number" },
                { header: "Техник", value: (order) => order.technicianName ?? "—" },
                { header: "Счёт", value: (order) => order.invoiceNumber ?? "—" },
            ];
            await exportExcel(`report-${companyName}-${this.params.dateFrom}-${this.params.dateTo}.xlsx`, [
                // Разрез «итого» уже содержит единственную строку-итог — «Итого» один раз (O-G3-3)
                excelSheet("Сводка", summaryColumns, this.params.groupBy === "total" ? [...headerRows, totalRow] : [...headerRows, ...breakdownRows, totalRow]),
                excelSheet("Реестр", registryColumns, this.orders),
            ]);
        }
        catch (error) {
            onB2BError(error, "Не удалось выгрузить отчёт в Excel");
        }
        finally {
            runInAction(() => {
                this.isExporting = false;
            });
        }
    }
    dispose() {
        this.generation += 1;
        this.companyId = null;
        this.params = defaultParams();
        this.report = null;
        this.orders = [];
        this.isLoading = false;
        this.isExporting = false;
    }
}
